/* eslint-disable react-refresh/only-export-components */

import React, { useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { useU2000GridStateHandlers } from "../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { useU2000SearchStateHandlers } from "../u2000Core/reducer/formReducer/U2000SearchStateHandlers";
import type { U2000GridStateHandlersReturn } from "../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { U2000SearchStateHandlersReturn } from "../u2000Core/reducer/formReducer/U2000SearchStateHandlers";
import { useU2000LUStateHandlers } from "../u2000Core/reducer/formReducer/U2000LUHandlers";
import type { U2000LUState } from "../u2000Core/reducer/formReducer/U2000LUState";
import type { U3104InitLU, U3104AccountingYearSearchRow } from "./U3104Service";

export type InitLU = U2000LUState["personalization"] &
  U3104InitLU & {
    launchSearch: boolean;
    hideSearch: boolean;
    nbRecordPerPage: number;
  };

export interface U3104ContextType {
  luStateHandlers: ReturnType<typeof useU2000LUStateHandlers>;
  searchStateHandlers: U2000SearchStateHandlersReturn;
  gridStateHandlers: U2000GridStateHandlersReturn<U3104AccountingYearSearchRow>;
  initLU: U3104InitLU;
}

interface U3104ContextProviderProps {
  initLU: U3104InitLU;
  children: ReactNode;
}

const U3104Context = React.createContext<U3104ContextType | undefined>(undefined);

export const U3104ContextProvider: React.FC<U3104ContextProviderProps> = ({ initLU, children }) => {
  const U2000LUStateHandlers = useU2000LUStateHandlers({ initLU: initLU as InitLU & U2000LUState });
  const U2000SearchStateHandlers = useU2000SearchStateHandlers({
    luName: "u3104",
    launchSearch: initLU.launchSearch,
    hideSearch: initLU.hideSearch,
  });
  const U2000GridStateHandlers = useU2000GridStateHandlers<U3104AccountingYearSearchRow>({
    nbRecordPerPage: initLU.nbRecordPerPage ?? 25,
  });

  const contextValue = useMemo<U3104ContextType>(
    () => ({
      luStateHandlers: U2000LUStateHandlers,
      searchStateHandlers: U2000SearchStateHandlers,
      gridStateHandlers: U2000GridStateHandlers,
      initLU,
    }),
    [U2000GridStateHandlers, U2000SearchStateHandlers, U2000LUStateHandlers, initLU],
  );

  return <U3104Context.Provider value={contextValue}>{children}</U3104Context.Provider>;
};

export function useU3104Context(): U3104ContextType {
  const context = useContext(U3104Context);
  if (!context) {
    throw new Error("useU3104Context must be used within a U3104ContextProvider");
  }
  return context;
}
